import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import '../App.css';

import Spinner from '../components/Spinner';
import { AlertIcon, CheckIcon, ChatIcon, MailIcon } from '../components/Icons';
import { BASE_URL } from '../App';
import { apiErrorMessage } from '../utils/format';
import { trackErrorShown, trackPaymentResultViewed } from '../utils/analytics';

const cx = (...parts) => parts.filter(Boolean).join(' ');

const POLL_INTERVAL_MS = 2000;
/* ~2 minutes. The old loop polled forever: a payment stuck in PENDING left the
   member staring at a spinner with no message and no way out. */
const MAX_ATTEMPTS = 60;

const RESULT_STATUS = { SUCCESS: 'success', FAILED: 'failed', TIMEOUT: 'pending' };

const PaymentCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const orderId = searchParams.get('order_id');

  const [status, setStatus] = useState('PENDING');
  const [error, setError] = useState(null);

  // Guards against setState/navigate after unmount — the previous cleanup was
  // `clearTimeout()` with no handle, so polling outlived the component.
  const cancelledRef = useRef(false);
  const timerRef = useRef(null);
  const reasonRef = useRef(null);

  // One payment_result_viewed per terminal state (PENDING is not a result yet).
  useEffect(() => {
    const resultStatus = RESULT_STATUS[status];
    if (!resultStatus) return;
    trackPaymentResultViewed({ orderId, status: resultStatus, reason: reasonRef.current });
    if (reasonRef.current === 'missing_order_id' || reasonRef.current === 'status_check_error') {
      trackErrorShown({ context: 'payment_callback', reason: reasonRef.current });
    }
  }, [status, orderId]);

  useEffect(() => {
    cancelledRef.current = false;

    if (!orderId) {
      reasonRef.current = 'missing_order_id';
      setError('We could not find an order reference in this link.');
      setStatus('FAILED');
      return undefined;
    }

    let attempts = 0;

    const checkPaymentStatus = async () => {
      if (cancelledRef.current) return;
      attempts += 1;

      try {
        const response = await fetch(
          `${BASE_URL}/partner/v2/get-portal-payment-status?order_id=${encodeURIComponent(
            orderId
          )}`,
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${localStorage.getItem('portalToken')}`,
            },
          }
        );

        const data = await response.json().catch(() => ({}));

        if (cancelledRef.current) return;

        if (!response.ok) {
          throw new Error(
            apiErrorMessage(data, 'We could not check your payment status.')
          );
        }

        if (data.order_status === 'SUCCESS') {
          setStatus('SUCCESS');
          timerRef.current = setTimeout(() => {
            if (cancelledRef.current) return;
            navigate('/order-details', {
              state: { orderId, paymentDetails: data.payment_attempts?.[0] },
            });
          }, 1200);
          return;
        }

        if (data.order_status === 'FAILED') {
          reasonRef.current = 'payment_failed';
          setStatus('FAILED');
          setError('Your payment did not go through. You have not been charged.');
          return;
        }

        if (attempts >= MAX_ATTEMPTS) {
          reasonRef.current = 'confirmation_timeout';
          setStatus('TIMEOUT');
          return;
        }

        timerRef.current = setTimeout(checkPaymentStatus, POLL_INTERVAL_MS);
      } catch (err) {
        if (cancelledRef.current) return;
        console.error('Error checking payment status:', err);
        reasonRef.current = 'status_check_error';
        setError(err.message);
        setStatus('FAILED');
      }
    };

    checkPaymentStatus();

    return () => {
      cancelledRef.current = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [orderId, navigate]);

  const supportRow = (
    <div className="supportRow" style={{ justifyContent: 'center' }}>
      <a
        className={cx('btn', 'btnSm', 'btnSecondary')}
        href={`https://wa.me/919148477518?text=${encodeURIComponent(
          `Payment question about order ${orderId || ''}`
        )}`}
        target="_blank"
        rel="noopener noreferrer"
      >
        <ChatIcon />
        WhatsApp
      </a>
      <a
        className={cx('btn', 'btnSm', 'btnSecondary')}
        href={`mailto:help@getfleek.app?subject=${encodeURIComponent(
          `Payment question - Order ID: ${orderId || ''}`
        )}`}
      >
        <MailIcon />
        Email
      </a>
    </div>
  );

  return (
    <div className="page">
      <div className="statusWrap">
        <div className="statusCard">
          {status === 'SUCCESS' && (
            <>
              <div className="statusIcon statusIconOk" aria-hidden="true">
                <CheckIcon width="28" height="28" />
              </div>
              <h1 className="statusTitle">Payment confirmed</h1>
              <p className="statusBody">Taking you to your order…</p>
            </>
          )}

          {status === 'PENDING' && (
            <>
              <div className="statusIcon statusIconWait" aria-hidden="true">
                <Spinner />
              </div>
              <h1 className="statusTitle">Confirming your payment</h1>
              <p className="statusBody">
                This usually takes a few seconds. Please don't close this tab.
              </p>
            </>
          )}

          {status === 'TIMEOUT' && (
            <>
              <div className="statusIcon statusIconWait" aria-hidden="true">
                <Spinner />
              </div>
              <h1 className="statusTitle">Still processing</h1>
              <p className="statusBody">
                Your bank is taking longer than usual to confirm. We'll email you
                as soon as it settles — no need to pay again.
              </p>
              <div className="statusActions">
                <button
                  type="button"
                  className={cx('btn', 'btnMd', 'btnSecondary')}
                  onClick={() => navigate('/marketplace')}
                >
                  Back to marketplace
                </button>
              </div>
              {supportRow}
            </>
          )}

          {status === 'FAILED' && (
            <>
              <div className="statusIcon statusIconBad" aria-hidden="true">
                <AlertIcon width="28" height="28" />
              </div>
              <h1 className="statusTitle">Payment not completed</h1>
              <p className="statusBody">{error}</p>
              <div className="statusActions">
                <button
                  type="button"
                  className={cx('btn', 'btnMd', 'btnBrand')}
                  onClick={() => navigate('/marketplace')}
                >
                  Back to marketplace
                </button>
              </div>
              {supportRow}
            </>
          )}

          {orderId && <div className="statusMeta">Order ID: {orderId}</div>}
        </div>
      </div>
    </div>
  );
};

export default PaymentCallback;
